<template>
  <v-container>
    <div class="d-flex flex-wrap justify-between align-center" style="gap: 10px">
      <h1>Product List</h1>

      <div
        class="d-flex justify-end"
        :class="$vuetify.display.mdAndDown ? 'flex-wrap' : 'ga-3'"
        style="gap: 0px; flex: 1"
      >
        <div
          v-if="userRole !== 'company_user'"
          :style="$vuetify.display.mdAndDown ? 'max-width:100%' : 'max-width:300px'"
          style="width: 100%; text-align: right"
        >
          <v-btn
            v-if="!['company_user', 'company_admin'].includes(userRole || '')"
            to="/products/add-product-template"
            variant="text"
            size="small"
            prepend-icon="mdi-plus"
            class="text-grey-darken-2"
            data-testid="add-product-btn"
          >
            Add new product
          </v-btn>
        </div>
      </div>
    </div>

    <p class="my-6 text-body-2 text-grey-darken-1">
      Click on a product row to view details and manage product settings.
    </p>

    <v-card>
      <v-toolbar color="primary" density="comfortable" flat>
        <div class="d-flex align-center text-white ml-2" style="gap: 12px"></div>
        <v-spacer></v-spacer>
        <!-- Desktop search (inline, compact) -->
        <v-text-field
          ref="searchInputRef"
          v-if="!$vuetify.display.mdAndDown"
          v-model="searchQuery"
          placeholder="Search by name"
          clearable
          @click:clear="searchQuery = ''"
          prepend-inner-icon="mdi-magnify"
          hide-details
          density="compact"
          variant="solo"
          class="search-in-bar mr-2"
          data-testid="product-search-input"
        ></v-text-field>
      </v-toolbar>
      <!-- Mobile search (full width on next line) -->
      <div class="px-2 pt-2 mb-2" v-if="$vuetify.display.mdAndDown">
        <v-text-field
          ref="searchInputRefMobile"
          v-model="searchQuery"
          placeholder="Search by name"
          clearable
          @click:clear="searchQuery = ''"
          prepend-inner-icon="mdi-magnify"
          hide-details
          density="comfortable"
          variant="solo"
          style="width: 100%; max-width: 100%"
        ></v-text-field>
      </div>
    </v-card>

    <v-data-table
      data-testid="products-table"
      :headers="headers"
      :items="rootFiltered"
      :page="page"
      :items-per-page="itemsPerPage"
      :loading="loading"
      item-value="path"
      class="elevation-1 sticky-header striped-rows even-cols min-height-table tree-table"
      :style="{ '--even-col-count': headers.length }"
      :row-class="rowClassForItem"
      @update:page="(p: number) => (page = p)"
      @update:items-per-page="(n: number) => (itemsPerPage = n)"
    >
      <template #loading>
        <div class="height-loader">
          <v-progress-circular indeterminate color="primary" />
        </div>
      </template>

      <template #body="{ items }">
        <template v-for="item in items" :key="item.path">
          <!-- Parent row -->
          <tr
            :class="['v-data-table__tr', rowClassForItem(item)]"
            style="cursor: pointer"
            @click="onProductRowClick({}, { item })"
          >
            <td class="v-data-table__td v-data-table-column--align-start">
              <div class="d-flex align-center" style="gap: 8px">
                <v-btn
                  v-if="item.hasChildren"
                  icon
                  size="x-small"
                  variant="text"
                  @click.stop="toggleExpand(item)"
                >
                  <v-icon size="small">
                    {{ expandedProducts.has(item.path) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                  </v-icon>
                </v-btn>
                <span v-else style="width: 28px; display: inline-block"></span>
                <span :class="{ 'text-grey': item.synthetic }">
                  {{ item.productFriendlyName || item.product }}
                </span>
              </div>
            </td>
            <td v-if="canSeeProductId" class="v-data-table__td v-data-table-column--align-start">
              <span :class="{ 'text-grey': item.synthetic }">
                {{ item.product || item.productId }}
              </span>
            </td>
            <td class="v-data-table__td v-data-table-column--align-start">
              <v-chip
                :color="
                  item.type === 'parent'
                    ? 'primary'
                    : item.type === 'intermediate'
                      ? 'secondary'
                      : 'default'
                "
                size="small"
                variant="tonal"
              >
                {{
                  item.type === 'parent'
                    ? 'Parent'
                    : item.type === 'intermediate'
                      ? 'Intermediate'
                      : 'Child'
                }}
              </v-chip>
            </td>
          </tr>

          <!-- Children rows (when expanded) -->
          <template v-if="expandedProducts.has(item.path) && item.hasChildren">
            <template
              v-for="(child, childIndex) in getChildrenInline(item.productId)"
              :key="child.path"
            >
              <tr
                style="cursor: pointer"
                :style="{ backgroundColor: childIndex % 2 === 0 ? '#e3f2fd' : 'white' }"
                @click="onChildInlineClick(child)"
              >
                <td class="v-data-table__td v-data-table-column--align-start">
                  <div class="d-flex align-center pl-8">
                    <v-btn
                      v-if="child.hasChildren"
                      icon
                      size="x-small"
                      variant="text"
                      @click.stop="toggleExpand(child)"
                    >
                      <v-icon size="small">
                        {{
                          expandedProducts.has(child.path)
                            ? 'mdi-chevron-down'
                            : 'mdi-chevron-right'
                        }}
                      </v-icon>
                    </v-btn>
                    <span v-else style="width: 28px; display: inline-block"></span>
                    <span>{{ child.productFriendlyName || child.productId }}</span>
                  </div>
                </td>
                <td
                  v-if="canSeeProductId"
                  class="v-data-table__td v-data-table-column--align-start"
                >
                  {{ child.product || child.productId }}
                </td>
                <td class="v-data-table__td v-data-table-column--align-start">
                  <v-chip
                    :color="child.type === 'intermediate' ? 'secondary' : 'default'"
                    size="small"
                    variant="tonal"
                    @click.stop="console.log('Child data:', child)"
                  >
                    {{ child.type === 'intermediate' ? 'Intermediate' : 'Child' }}
                  </v-chip>
                </td>
              </tr>

              <!-- Grandchildren rows (if intermediate product is expanded) -->
              <template v-if="expandedProducts.has(child.path) && child.hasChildren">
                <template
                  v-for="(grandchild, grandIndex) in getChildrenInline(child.productId)"
                  :key="grandchild.path"
                >
                  <tr
                    style="cursor: pointer"
                    :style="{ backgroundColor: grandIndex % 2 === 0 ? '#f5f5f5' : 'white' }"
                    @click="onChildInlineClick(grandchild)"
                  >
                    <td class="v-data-table__td v-data-table-column--align-start">
                      <div class="d-flex align-center pl-16">
                        <v-btn
                          v-if="grandchild.hasChildren"
                          icon
                          size="x-small"
                          variant="text"
                          @click.stop="toggleExpand(grandchild)"
                        >
                          <v-icon size="small">
                            {{
                              expandedProducts.has(grandchild.path)
                                ? 'mdi-chevron-down'
                                : 'mdi-chevron-right'
                            }}
                          </v-icon>
                        </v-btn>
                        <span v-else style="width: 28px; display: inline-block"></span>
                        <span>{{ grandchild.productFriendlyName || grandchild.productId }}</span>
                      </div>
                    </td>
                    <td
                      v-if="canSeeProductId"
                      class="v-data-table__td v-data-table-column--align-start"
                    >
                      {{ grandchild.product || grandchild.productId }}
                    </td>
                    <td class="v-data-table__td v-data-table-column--align-start">
                      <v-chip
                        :color="grandchild.type === 'intermediate' ? 'secondary' : 'default'"
                        size="small"
                        variant="tonal"
                        @click.stop="console.log('Grandchild data:', grandchild)"
                      >
                        {{ grandchild.type === 'intermediate' ? 'Intermediate' : 'Child' }}
                      </v-chip>
                    </td>
                  </tr>

                  <!-- Great-grandchildren rows (if grandchild intermediate is expanded) -->
                  <template v-if="expandedProducts.has(grandchild.path) && grandchild.hasChildren">
                    <tr
                      v-for="(greatGrandchild, ggIndex) in getChildrenInline(grandchild.productId)"
                      :key="greatGrandchild.path"
                      style="cursor: pointer"
                      :style="{ backgroundColor: ggIndex % 2 === 0 ? '#eeeeee' : 'white' }"
                      @click="onChildInlineClick(greatGrandchild)"
                    >
                      <td class="v-data-table__td v-data-table-column--align-start">
                        <div class="d-flex align-center pl-16" style="gap: 8px; margin-left: 72px">
                          <span>
                            {{ greatGrandchild.productFriendlyName || greatGrandchild.productId }}
                          </span>
                        </div>
                      </td>
                      <td
                        v-if="canSeeProductId"
                        class="v-data-table__td v-data-table-column--align-start"
                      >
                        {{ greatGrandchild.product || greatGrandchild.productId }}
                      </td>
                      <td class="v-data-table__td v-data-table-column--align-start">
                        <v-chip color="default" size="small" variant="tonal">Child</v-chip>
                      </td>
                    </tr>
                  </template>
                </template>
              </template>
            </template>
          </template>
        </template>
      </template>

      <template #no-data>
        <div class="text-center pa-4">No products available</div>
      </template>
    </v-data-table>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/userStore'
import { fetchAllProductsFromIndex } from '@/api/accountsAdmin'
import { VerifyAdmin } from '@/api/accountsAdmin'

const router = useRouter()
const userStore = useUserStore()

const userRole = ref(userStore?.role)
const searchInputRef = ref<any>(null)
const searchInputRefMobile = ref<any>(null)
const loading = ref(false)
const searchQuery = ref('')
// Status filter removed for this table

// All products loaded from hierarchy index
const allProducts = ref<any[]>([])

// Track which products are expanded
const expandedProducts = ref<Set<string>>(new Set())

// Role-based visibility for Product ID column
const canSeeProductId = computed(() =>
  ['company_suadmin', 'company_owner'].includes(String(userRole.value))
)

// Table headers
const headers = computed(() => {
  const cols: any[] = [
    { title: 'Product Name Friendly', value: 'productFriendlyName', sortable: false },
  ]
  if (canSeeProductId.value) {
    cols.push({ title: 'Product ID', value: 'productId', sortable: false, align: 'start' })
  }
  cols.push({ title: 'Type', value: 'type', sortable: false, align: 'start', width: '10%' })
  return cols
})

// Pagination state (root-level only)
const page = ref(1)
const itemsPerPage = ref(25)

// Compute root products after filters
const rootFiltered = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()

  // If no search query, show only root products
  if (!q) {
    return allProducts.value.filter((p) => !p.parentPath)
  }

  // If searching, include parents whose children match
  const matchingProducts = new Set<string>()
  const parentsToInclude = new Set<string>()

  allProducts.value.forEach((p) => {
    const matches =
      (p.productFriendlyName || '').toLowerCase().includes(q) ||
      (p.productId || '').toLowerCase().includes(q) ||
      (p.path || '').toLowerCase().includes(q)

    if (matches) {
      matchingProducts.add(p.path)
      // If this is a child, include its parent
      if (p.parentPath) {
        parentsToInclude.add(p.parentPath)
      }
    }
  })

  // Return root products that either match or have matching children
  return allProducts.value.filter((p) => {
    if (p.parentPath) return false // Still only show roots in the main list
    return matchingProducts.has(p.path) || parentsToInclude.has(p.productId)
  })
})
const totalRootCount = computed(() => rootFiltered.value.length)

// Build rows for the current page: include children of roots on this page
const rootsForPage = computed(() => {
  const start = (page.value - 1) * itemsPerPage.value
  return rootFiltered.value.slice(start, start + itemsPerPage.value)
})

// Helpers for inline children
// Note: Despite the parameter name, this receives a product's simple ID (productId)
// and returns children whose parentPath matches that ID
function getChildrenInline(parentProductId: string) {
  const q = searchQuery.value.trim().toLowerCase()
  const children = allProducts.value.filter((p) => p.parentPath === parentProductId)

  // Debug log for all parent's children
  if (children.length > 0) {
    console.log(
      `Children of ${parentProductId}:`,
      children.map((c) => ({
        id: c.productId,
        type: c.type,
        hasChildren: c.hasChildren,
        parentPath: c.parentPath,
      }))
    )
  }

  // If searching, filter children to only show matching ones
  if (!q) return children

  return children.filter(
    (p) =>
      (p.productFriendlyName || '').toLowerCase().includes(q) ||
      (p.productId || '').toLowerCase().includes(q) ||
      (p.path || '').toLowerCase().includes(q)
  )
}
function onChildInlineClick(child: any) {
  const path = String(child?.path || child?.productId || child?.product || '')
  if (!path) return
  if (path.includes('/'))
    router.push(`/products/view/child-product?query=${encodeURIComponent(path)}`)
  else router.push(`/products/view/${path}`)
}

// Base set used for counts: apply search query but ignore status toggles
const searchFilteredProducts = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  if (!q) return allProducts.value
  return allProducts.value.filter(
    (p) =>
      (p.productFriendlyName || '').toLowerCase().includes(q) ||
      (p.productId || '').toLowerCase().includes(q) ||
      (p.path || '').toLowerCase().includes(q)
  )
})

// Status counts removed with filter UI

// Filtered products (search only; removed active/inactive toggles)
const filteredProducts = computed(() => searchFilteredProducts.value)

// Build tree structure with expansion
const flattenedRows = computed(() => {
  const result: any[] = []
  const productMap = new Map(filteredProducts.value.map((p) => [p.path, p]))

  // Get root products (no parent)
  const roots = filteredProducts.value.filter((p) => !p.parentPath)

  const addProductAndChildren = (product: any, level: number) => {
    const isExpanded = expandedProducts.value.has(product.path)

    // Add the product
    result.push({
      ...product,
      level,
      expanded: isExpanded,
    })

    // If expanded and has children, add them
    if (isExpanded && product.hasChildren) {
      // Match children by simple productId, not full path
      const children = filteredProducts.value.filter((p) => p.parentPath === product.productId)
      for (const child of children) {
        addProductAndChildren(child, level + 1)
      }
    }
  }

  // Add all root products and their children
  for (const root of roots) {
    addProductAndChildren(root, 0)
  }

  return result
})

// Toggle expand/collapse
const toggleExpand = (item: any) => {
  if (expandedProducts.value.has(item.path)) {
    expandedProducts.value.delete(item.path)
  } else {
    expandedProducts.value.add(item.path)
  }
}

// Row class for styling
const rowClassForItem = (item: any) => {
  const classes = []
  if (item.isLoadingRow) classes.push('loading-row')
  if (item.synthetic) classes.push('synthetic-row')
  if (item.level > 0) classes.push('child-row')
  return classes.join(' ')
}

// Navigate to product details
const onProductRowClick = (_: any, row: any) => {
  const item = row.item
  if (item.isLoadingRow || item.synthetic) return

  const path = String(item?.path || item?.productId || item?.product || '')
  if (!path) return

  if (path.includes('/')) {
    router.push(`/products/view/child-product?query=${encodeURIComponent(path)}`)
  } else {
    router.push(`/products/view/${path}`)
  }
}

// Load all products from hierarchy index
const loadProducts = async () => {
  loading.value = true
  try {
    // Clear cache before loading to ensure fresh data
    try {
      accountStore.setProductsCache([])
    } catch (e) {
      console.warn('Could not clear products cache:', e)
    }

    const response = await fetchAllProductsFromIndex()
    // Force a deep copy to ensure Vue reactivity picks up all changes
    allProducts.value = JSON.parse(JSON.stringify(response.products || []))
    console.log(`Loaded ${allProducts.value.length} products from hierarchy index`)

    // Debug: Log intermediate products
    const intermediates = allProducts.value.filter((p: any) => p.type === 'intermediate')
    console.log(
      `Found ${intermediates.length} intermediate products:`,
      intermediates.map((p: any) => ({
        id: p.productId,
        type: p.type,
        parentPath: p.parentPath,
        hasChildren: p.hasChildren,
      }))
    )
  } catch (error) {
    console.error('Failed to load products:', error)
  } finally {
    loading.value = false
  }
}

// Auto-expand parents when their children match the search
watch(searchQuery, (newQuery) => {
  const q = newQuery.trim().toLowerCase()
  if (!q) {
    expandedProducts.value.clear()
    return
  }

  // Auto-expand all ancestors when a product matches
  allProducts.value.forEach((p) => {
    const matches =
      (p.productFriendlyName || '').toLowerCase().includes(q) ||
      (p.productId || '').toLowerCase().includes(q) ||
      (p.path || '').toLowerCase().includes(q)

    if (matches && p.parentPath) {
      // Expand all ancestors up the tree
      let currentParentId = p.parentPath
      while (currentParentId) {
        const parent = allProducts.value.find((prod) => prod.productId === currentParentId)
        if (parent) {
          expandedProducts.value.add(parent.path)
          currentParentId = parent.parentPath // Continue up the tree
        } else {
          break
        }
      }
    }
  })
})

onMounted(() => {
  VerifyAdmin()
  loadProducts()
  nextTick(() => {
    // Focus desktop input if visible; otherwise focus mobile input
    if (searchInputRef.value?.focus) {
      searchInputRef.value.focus()
    } else if (searchInputRefMobile.value?.focus) {
      searchInputRefMobile.value.focus()
    }
  })
})
</script>

<style scoped>
.search-in-bar {
  max-width: 300px;
}

.height-loader {
  min-height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tree-table :deep(.child-row) {
  background-color: rgba(0, 0, 0, 0.02);
}

.tree-table :deep(.synthetic-row) {
  opacity: 0.6;
  font-style: italic;
}

.tree-table :deep(.loading-row) {
  cursor: default !important;
  background-color: rgba(0, 0, 0, 0.03);
}

.sticky-header :deep(thead) {
  position: sticky;
  top: 0;
  z-index: 10;
  background: white;
}

.striped-rows :deep(tbody tr:nth-of-type(even)) {
  background-color: rgba(0, 0, 0, 0.02);
}

/* Hover background for all rows */
.tree-table :deep(tbody tr:hover) {
  background-color: rgba(33, 150, 243, 0.08) !important; /* light primary; override inline bg */
}

.min-height-table {
  min-height: 400px;
}
</style>
